import type { CheckTemplate, DraftQuestionInput, QuestionSeverity } from '@my/check-api';
import { Button, ConfirmDialog } from '@my/ui';
import { ArrowLeft, Eye, GripVertical, Plus, Save, Send, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SEVERITY_OPTIONS,
  SEVERITY_SELECT_CLASS,
  sectionLabelFrom,
  sectionsInOrder,
  toEditable,
  unitLabel,
  type EditableQuestion,
} from './lib/templateTypes';

interface TemplateEditorProps {
  template: CheckTemplate;
  isSaving: boolean;
  isDeleting: boolean;
  onBack: () => void;
  onPreview: (questions: DraftQuestionInput[]) => void;
  onSaveDraft: (questions: DraftQuestionInput[], name: string) => void;
  onPublish: (questions: DraftQuestionInput[], name: string) => void;
  onDelete: () => void;
}

type DropTarget = { key: string; edge: 'above' | 'below' };

/**
 * Step 2 — the question editor. Sections rail on the left, question rows on
 * the right grouped by section.
 *
 * Edits are held locally and pushed on Save: the draft-update endpoint
 * replaces the whole question list in one write, so batching here matches the
 * BE rather than firing a request per keystroke.
 *
 * Deleting the template lives here rather than on the grid — one click away
 * from a list of rows is too easy to hit by accident.
 *
 * Reordering is drag-and-drop using native HTML5 events, the same approach as
 * the TMS order task list: the grip arms the drag on mousedown so a
 * permanently-draggable row doesn't hijack text selection. Dropping onto a row
 * in a different section moves the question into that section. The ▲▼ arrows
 * stay as a keyboard-reachable fallback.
 */
export function TemplateEditor({
  template,
  isSaving,
  isDeleting,
  onBack,
  onPreview,
  onSaveDraft,
  onPublish,
  onDelete,
}: TemplateEditorProps) {
  const { t } = useTranslation('checks');

  const [name, setName] = useState(template.name);
  const [questions, setQuestions] = useState<EditableQuestion[]>(() =>
    template.questions.map(toEditable),
  );
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<EditableQuestion | null>(null);
  const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState(false);

  // Drag state. `dragKey` is the row being moved; `dropTarget` is the row it is
  // currently hovering and which edge of it.
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  // Re-seed when the server hands back a different version — publishing forks
  // a new one, so the ids in local state would otherwise be stale.
  useEffect(() => {
    setName(template.name);
    setQuestions(template.questions.map(toEditable));
  }, [template.versionId, template.name, template.questions]);

  const isType2 = template.wacType === 'Type2';
  const sections = useMemo(() => sectionsInOrder(questions), [questions]);
  const countBySection = useMemo(() => {
    const m = new Map<string, number>();
    for (const q of questions) m.set(q.sectionKey, (m.get(q.sectionKey) ?? 0) + 1);
    return m;
  }, [questions]);

  /** Renumber sortOrder contiguously so the BE receives a clean sequence. */
  const toPayload = (list: EditableQuestion[]): DraftQuestionInput[] =>
    list.map((q, i) => ({
      catalogueKey: q.catalogueKey,
      sectionKey: q.sectionKey,
      sectionLabel: q.sectionLabel,
      label: q.label.trim(),
      sortOrder: i + 1,
      isMandatory: q.isMandatory,
      defaultSeverity: q.defaultSeverity,
      allowNotApplicable: q.allowNotApplicable,
      requirePhotoOnDefect: q.requirePhotoOnDefect,
    }));

  const patch = (key: string, p: Partial<EditableQuestion>) =>
    setQuestions((prev) => prev.map((q) => (q.key === key ? { ...q, ...p } : q)));

  const removeNow = (key: string) =>
    setQuestions((prev) => prev.filter((q) => q.key !== key));

  /**
   * Custom and optional questions go immediately — a confirm on every row
   * would be noise. Only a standard catalogue item marked mandatory is worth
   * stopping for.
   */
  const requestRemove = (q: EditableQuestion) => {
    if (q.catalogueKey !== null && q.isMandatory) setPendingRemove(q);
    else removeNow(q.key);
  };

  /** Arrow fallback. Stays inside a section; cross-section moves use drag. */
  const move = (key: string, dir: -1 | 1) =>
    setQuestions((prev) => {
      const list = [...prev];
      const i = list.findIndex((q) => q.key === key);
      const j = i + dir;
      const a = list[i];
      const b = list[j];
      if (!a || !b || a.sectionKey !== b.sectionKey) return prev;
      list[i] = b;
      list[j] = a;
      return list;
    });

  const endDrag = () => {
    setDragKey(null);
    setDropTarget(null);
  };

  /**
   * Move the dragged row to the drop position. The row adopts the target's
   * section, so dragging across a section heading re-files the question —
   * which is the only way to do that, since the arrows are section-bounded.
   */
  const handleDrop = () => {
    if (!dragKey || !dropTarget || dragKey === dropTarget.key) {
      endDrag();
      return;
    }
    setQuestions((prev) => {
      const moved = prev.find((q) => q.key === dragKey);
      const target = prev.find((q) => q.key === dropTarget.key);
      if (!moved || !target) return prev;

      const without = prev.filter((q) => q.key !== dragKey);
      const targetIndex = without.findIndex((q) => q.key === dropTarget.key);
      if (targetIndex < 0) return prev;

      without.splice(dropTarget.edge === 'above' ? targetIndex : targetIndex + 1, 0, {
        ...moved,
        sectionKey: target.sectionKey,
        sectionLabel: target.sectionLabel,
      });
      return without;
    });
    endDrag();
  };

  const addCustom = (sectionKey: string) =>
    setQuestions((prev) => {
      const label = sectionLabelFrom(prev, sectionKey);
      const lastIndex = prev.reduce((acc, q, i) => (q.sectionKey === sectionKey ? i : acc), -1);
      const row: EditableQuestion = {
        id: null,
        key: `new-${crypto.randomUUID()}`,
        catalogueKey: null,
        sectionKey,
        sectionLabel: label,
        label: '',
        sortOrder: 0,
        isMandatory: false,
        defaultSeverity: 'Medium',
        allowNotApplicable: false,
        // Off by default, Type 2 included — see the tick on the question row.
        requirePhotoOnDefect: false,
      };
      const next = [...prev];
      next.splice(lastIndex + 1, 0, row);
      return next;
    });

  const hasBlankLabel = questions.some((q) => q.label.trim().length === 0);
  const canPublish = !isSaving && questions.length > 0 && !hasBlankLabel && name.trim().length > 0;
  const busy = isSaving || isDeleting;

  let rowNumber = 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
        <Button variant="ghost" size="xs" leftIcon={ArrowLeft} onClick={onBack} disabled={busy}>
          {t('templates.editor.back')}
        </Button>

        <input
          type="text"
          value={name}
          maxLength={128}
          onChange={(e) => setName(e.target.value)}
          aria-label={t('templates.new.nameLabel')}
          className="min-w-[14rem] rounded-sm border border-transparent bg-transparent px-1 py-0.5 text-sm font-semibold text-slate-900 hover:border-slate-300 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
        />

        <span className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-medium uppercase text-slate-600">
          {unitLabel(template.unitType)}
          {template.wacType
            ? ` · ${t(template.wacType === 'Type1' ? 'templates.wac.type1' : 'templates.wac.type2')}`
            : ''}
          {` · v${template.version}`}
        </span>

        {template.hasUnpublishedDraft && (
          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800">
            {t('templates.editor.unpublished')}
          </span>
        )}

        <div className="flex-1" />
        <span className="text-[10px] text-slate-500">
          {t('templates.editor.questionCount', { count: questions.length })}
        </span>

        {/* Destructive action sits apart from the save cluster, on the far side
            of the count, so it is not adjacent to Save & publish. */}
        <Button
          variant="ghost"
          size="xs"
          leftIcon={Trash2}
          disabled={busy}
          onClick={() => setConfirmDeleteTemplate(true)}
          className="text-red-600 hover:bg-red-50"
        >
          {t('templates.editor.deleteTemplate')}
        </Button>

        <Button
          variant="outline"
          size="xs"
          leftIcon={Eye}
          onClick={() => onPreview(toPayload(questions))}
        >
          {t('templates.editor.driverPreview')}
        </Button>
        <Button
          variant="outline"
          size="xs"
          leftIcon={Save}
          disabled={busy || !name.trim()}
          onClick={() => onSaveDraft(toPayload(questions), name.trim())}
        >
          {t('templates.editor.saveDraft')}
        </Button>
        <Button
          size="xs"
          leftIcon={Send}
          disabled={!canPublish || isDeleting}
          title={hasBlankLabel ? t('templates.editor.blankLabels') : undefined}
          onClick={() => onPublish(toPayload(questions), name.trim())}
        >
          {t('templates.editor.savePublish')}
        </Button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 pb-4 pt-3 lg:grid-cols-[minmax(160px,200px)_1fr]">
        <nav className="min-h-0 lg:h-full lg:overflow-y-auto">
          <p className="px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
            {t('templates.editor.sections')}
          </p>
          <ul className="space-y-0.5">
            {/* Explicit "All sections" row. Clicking the active section again
                also clears the filter, but that is not discoverable — there
                needs to be something obvious to click to get back. */}
            <li>
              <button
                type="button"
                onClick={() => setActiveSection(null)}
                className={`flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors ${
                  activeSection === null
                    ? 'bg-blue-50 font-semibold text-blue-700'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <span className="min-w-0 truncate">{t('templates.editor.allSections')}</span>
                <span className="flex-shrink-0 rounded-full bg-slate-200 px-1.5 text-[10px] font-medium text-slate-600">
                  {questions.length}
                </span>
              </button>
            </li>
            {sections.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onClick={() => setActiveSection(activeSection === s ? null : s)}
                  className={`flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors ${
                    activeSection === s
                      ? 'bg-blue-50 font-semibold text-blue-700'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="min-w-0 truncate">{sectionLabelFrom(questions, s)}</span>
                  <span className="flex-shrink-0 rounded-full bg-slate-200 px-1.5 text-[10px] font-medium text-slate-600">
                    {countBySection.get(s) ?? 0}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-h-0 space-y-4 lg:h-full lg:overflow-y-auto">
          {sections
            .filter((s) => activeSection === null || s === activeSection)
            .map((s) => {
              const rows = questions.filter((q) => q.sectionKey === s);
              return (
                <section key={s}>
                  <div className="mb-1.5 flex items-center gap-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                      {sectionLabelFrom(questions, s)}
                    </h3>
                    <span className="rounded-full bg-slate-200 px-1.5 text-[10px] font-medium text-slate-600">
                      {rows.length}
                    </span>
                    <div className="flex-1" />
                    <button
                      type="button"
                      onClick={() => addCustom(s)}
                      className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium text-blue-600 hover:bg-blue-50"
                    >
                      <Plus className="h-3 w-3" />
                      {t('templates.editor.addQuestion')}
                    </button>
                  </div>
                  <ul className="space-y-1.5">
                    {rows.map((q) => {
                      rowNumber += 1;
                      return (
                        <QuestionRow
                          key={q.key}
                          index={rowNumber}
                          question={q}
                          isType2={isType2}
                          canMoveUp={rows[0]?.key !== q.key}
                          canMoveDown={rows[rows.length - 1]?.key !== q.key}
                          isDragSource={dragKey === q.key}
                          dropEdge={dropTarget?.key === q.key ? dropTarget.edge : null}
                          onDragStart={() => setDragKey(q.key)}
                          onDragEnd={endDrag}
                          onDragOverEdge={(edge) => setDropTarget({ key: q.key, edge })}
                          onDragLeave={() =>
                            setDropTarget((prev) => (prev?.key === q.key ? null : prev))
                          }
                          onDropRow={handleDrop}
                          onMoveUp={() => move(q.key, -1)}
                          onMoveDown={() => move(q.key, 1)}
                          onPatch={(p) => patch(q.key, p)}
                          onRemove={() => requestRemove(q)}
                        />
                      );
                    })}
                  </ul>
                </section>
              );
            })}
        </div>
      </div>

      <ConfirmDialog
        open={pendingRemove !== null}
        tone="danger"
        title={t('templates.editor.removeStandardTitle')}
        message={
          pendingRemove
            ? t('templates.editor.removeStandardMessage', { label: pendingRemove.label })
            : null
        }
        confirmLabel={t('templates.editor.removeStandardConfirm')}
        onConfirm={() => {
          if (pendingRemove) removeNow(pendingRemove.key);
          setPendingRemove(null);
        }}
        onCancel={() => setPendingRemove(null)}
      />

      <ConfirmDialog
        open={confirmDeleteTemplate}
        tone="danger"
        title={t('templates.grid.deleteTitle')}
        message={t('templates.grid.deleteMessage', { name: template.name })}
        confirmLabel={t('templates.grid.delete')}
        busy={isDeleting}
        onConfirm={() => {
          setConfirmDeleteTemplate(false);
          onDelete();
        }}
        onCancel={() => setConfirmDeleteTemplate(false)}
      />
    </div>
  );
}

function QuestionRow({
  index,
  question,
  isType2,
  canMoveUp,
  canMoveDown,
  isDragSource,
  dropEdge,
  onDragStart,
  onDragEnd,
  onDragOverEdge,
  onDragLeave,
  onDropRow,
  onMoveUp,
  onMoveDown,
  onPatch,
  onRemove,
}: {
  index: number;
  question: EditableQuestion;
  isType2: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  isDragSource: boolean;
  dropEdge: 'above' | 'below' | null;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOverEdge: (edge: 'above' | 'below') => void;
  onDragLeave: () => void;
  onDropRow: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onPatch: (p: Partial<EditableQuestion>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('checks');
  const isCatalogue = question.catalogueKey !== null;

  // A permanently draggable row hijacks text selection in the custom-question
  // input, so the grip arms the drag on mousedown and disarms on drag end.
  const [dragArmed, setDragArmed] = useState(false);

  const edgeClass =
    dropEdge === 'above'
      ? 'border-t-2 border-t-blue-500'
      : dropEdge === 'below'
        ? 'border-b-2 border-b-blue-500'
        : '';

  return (
    <li
      draggable={dragArmed}
      onDragStart={(e) => {
        // Firefox refuses a drag with no payload.
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', question.key);
        onDragStart();
      }}
      onDragEnd={() => {
        setDragArmed(false);
        onDragEnd();
      }}
      onDragOver={(e) => {
        if (isDragSource) return;
        // Without preventDefault the browser refuses the drop outright.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const box = e.currentTarget.getBoundingClientRect();
        onDragOverEdge(e.clientY < box.top + box.height / 2 ? 'above' : 'below');
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        if (isDragSource) return;
        e.preventDefault();
        onDropRow();
      }}
      className={`flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-2 py-1.5 transition-colors ${
        isDragSource ? 'opacity-40' : ''
      } ${edgeClass}`}
    >
      <span
        onMouseDown={() => setDragArmed(true)}
        onMouseUp={() => setDragArmed(false)}
        title={t('templates.editor.dragHint')}
        className="cursor-grab text-slate-300 hover:text-slate-500 active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </span>
      <span className="w-6 flex-shrink-0 text-right text-[10px] text-slate-400">{index}</span>

      {isCatalogue ? (
        <span className="min-w-0 flex-1 truncate text-xs text-slate-800" title={question.label}>
          {question.label}
        </span>
      ) : (
        <input
          type="text"
          value={question.label}
          maxLength={256}
          placeholder={t('templates.editor.customPlaceholder')}
          onChange={(e) => onPatch({ label: e.target.value })}
          className="min-w-0 flex-1 rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 placeholder:italic placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      )}

      {isCatalogue ? (
        <span
          title={t('templates.editor.mandatoryLocked')}
          className="flex-shrink-0 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium uppercase text-red-600"
        >
          {t('templates.editor.mandatory')}
        </span>
      ) : (
        <label className="flex flex-shrink-0 items-center gap-1 text-[10px] text-slate-600">
          <input
            type="checkbox"
            checked={question.isMandatory}
            onChange={(e) => onPatch({ isMandatory: e.target.checked })}
            className="h-3 w-3 rounded border-slate-300"
          />
          {t('templates.editor.mandatory')}
        </label>
      )}

      <label
        title={t('templates.editor.allowNaHint')}
        className="flex flex-shrink-0 items-center gap-1 text-[10px] text-slate-600"
      >
        <input
          type="checkbox"
          checked={question.allowNotApplicable}
          onChange={(e) => onPatch({ allowNotApplicable: e.target.checked })}
          className="h-3 w-3 rounded border-slate-300"
        />
        {t('templates.editor.allowNa')}
      </label>

      {/* Per question, because it is only worth it where a picture shows
          something words cannot — damage, a leak — and wasted on "alarm works". */}
      <label
        title={t('templates.editor.photoRequiredHint')}
        className="flex flex-shrink-0 items-center gap-1 text-[10px] text-slate-600"
      >
        <input
          type="checkbox"
          checked={question.requirePhotoOnDefect}
          onChange={(e) => onPatch({ requirePhotoOnDefect: e.target.checked })}
          className="h-3 w-3 rounded border-slate-300"
        />
        {t('templates.editor.photoRequired')}
      </label>

      {isType2 && (
        <select
          value={question.defaultSeverity}
          aria-label={t('templates.editor.defaultSeverity')}
          title={t('templates.editor.defaultSeverity')}
          onChange={(e) => onPatch({ defaultSeverity: e.target.value as QuestionSeverity })}
          className={`flex-shrink-0 rounded-sm border bg-white px-1.5 py-0.5 text-[10px] font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 ${
            SEVERITY_SELECT_CLASS[question.defaultSeverity]
          }`}
        >
          {SEVERITY_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      )}

      <span className="flex flex-shrink-0 items-center">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!canMoveUp}
          aria-label={t('templates.editor.moveUp')}
          className="px-0.5 text-[10px] text-slate-400 hover:text-slate-700 disabled:opacity-30"
        >
          ▲
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!canMoveDown}
          aria-label={t('templates.editor.moveDown')}
          className="px-0.5 text-[10px] text-slate-400 hover:text-slate-700 disabled:opacity-30"
        >
          ▼
        </button>
      </span>

      <button
        type="button"
        onClick={onRemove}
        aria-label={t('templates.editor.removeQuestion')}
        className="flex-shrink-0 text-slate-300 transition-colors hover:text-red-500"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}
